/**
 * Theater mode: full-bleed video stage + the
 * constant right rail. The rail's tabs switch LOCALLY — flipping to
 * Cars never leaves theater, and picking a car there swaps the
 * followed car while staying in theater. The Driver tab's media slot
 * shows a MINI-MAP (the stage owns the video).
 *
 * Mobile (<lg): stacks — video above, rail below.
 */

import React, { useState } from 'react';

import { cn } from '@/lib/utils';
import { isLiveNow } from '../../hooks/useEventLiveDevices';
import BrowseMode from './BrowseMode';
import DriverTab from './DriverTab';
import FeedPlayer from './FeedPlayer';
import { useNowTick } from './lag';
import MiniMap from './MiniMap';
import RightPanel, { PanelTab } from './RightPanel';
import { ModeToggle } from './ViewerModeRouter';
// type-only: avoids a runtime circular import (router renders the modes)
import type { ViewerController } from './ViewerModeRouter';

export const TheaterMode: React.FC<{ c: ViewerController }> = ({ c }) => {
  // LOCAL tab state — switching tabs here is NOT a mode change.
  const [localTab, setLocalTab] = useState<PanelTab>('driver');
  const nowMs = useNowTick(1000);
  const device = c.selected;
  if (!device) return null; // router guards; satisfy the type system
  // lapOverride mounts an older session as VOD — live SSE speed must
  // not overlay replay footage (review).
  const isLive = !c.replay && isLiveNow(device, nowMs) && c.lapOverride == null;

  return (
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      {/* Stage — full-bleed video */}
      <div className="relative flex min-h-0 shrink-0 flex-col bg-bg-video lg:min-w-0 lg:flex-1">
        <ModeToggle
          mode="theater"
          hasCar
          onBrowse={c.goBrowse}
          onTheater={() => undefined}
          className="absolute right-3.5 top-3.5 z-20"
        />
        <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto">
          {/* Keyed on session_id — never reuse a player across
              sessions (same invariant as DetailMode's media slot). */}
          <FeedPlayer
            key={c.mountedPlayerKey}
            sessionId={c.mountedSessionId}
            playerRef={c.playerRef}
            onUtcUpdate={c.handlePlayheadUtc}
            onVideoEdgeUpdate={c.onPlayerEdge}
            onPdtReady={c.handlePdtReady}
            initialSeekUtcMs={c.initialSeekUtcMs}
            entryIntent={c.entryIntent}
            vod={c.playerVod}
            onRecordingStatus={c.onRecordingStatus}
            badge={c.videoBadge}
            fill
            cornerChip={
              isLive && device.speed_mps != null
                ? `${Math.round(device.speed_mps * 3.6)} km/h`
                : null
            }
            glassRail={c.glassRail}
          />
        </div>
      </div>

      {/* Rail — identical panel shell; tabs switch locally */}
      <RightPanel
        active={localTab}
        selected={device}
        onTabChange={setLocalTab}
        className={cn(
          'min-h-0 flex-1 border-t border-border-subtle',
          'lg:flex-none lg:border-t-0 lg:w-[400px] lg:border-l',
        )}
      >
        {localTab === 'cars' ? (
          <BrowseMode
            c={c}
            // Local selection: swap the followed car, stay in theater.
            onSelectCar={(id) => {
              c.selectCar(id, 'theater');
              setLocalTab('driver');
            }}
          />
        ) : (
          <DriverTab
            device={device}
            replay={c.replay}
            recordingStatus={c.recordingStatus}
            playbackSpeedKmh={c.playbackSpeedKmh}
            trailTrack={c.trailTrack}
            trailCoverage={c.trailCoverage}
            playheadUtc={c.playheadUtc}
            onSeek={c.handleSeek}
            onClose={c.goBrowse}
            videoStartMs={c.videoStartMs}
            media={
              <MiniMap
                device={device}
                racelineCoords={c.racelineCoords}
                trailTrack={c.trailTrack}
                playheadUtc={c.playheadUtc}
                onSeek={c.handleSeek}
                label={c.event.name}
              />
            }
            playerLag={c.playerLagInfo}
            stageProgress={c.stageProgress}
            stageLengthM={c.stageLengthM}
            compareCapable={c.compareCapable}
            compareBusy={c.compareBusy}
            onCompareOpen={c.handleCompareOpen}
            compareRun={c.compareRun}
            compareePlayerRef={c.compareePlayerRef}
            followed={c.followedIds.has(device.device_id)}
            onToggleFollow={c.toggleFollow}
            onCompareClose={c.handleCompareClose}
            isCircuit={c.isCircuit}
            laps={c.laps}
            selectedLapIdx={c.selectedLapIdx}
            onPickLap={c.handlePickLap}
            onPickLive={c.handlePickLive}
            stageEvents={c.stageEvents}
            sessions={c.driverSessions}
            onPickSession={c.handlePickSession}
            mountedSessionId={c.mountedSessionId}
          />
        )}
      </RightPanel>
    </div>
  );
};

export default TheaterMode;
